import { DateTime } from 'luxon';
import type { JSX } from 'react';
import { AdditionalInfoMode } from '../../common/state/PermitTypeState.ts';
import { DatePickerSlideIn } from '../../ui/slidein/DatePickerSlideIn.tsx';
import { useStore } from 'dg-web-shared/lib/Flux.tsx';
import { Formatter, isUnlimitedToDate } from 'dg-web-shared/lib/Date.ts';
import { Maybe } from 'dg-web-shared/lib/MaybeV2.ts';
import { Translation } from '../../common/i18n/Text.ts';
import * as OperatorDataState from '../../common/state/OperatorDataState.ts';
import { FieldSetting } from '../../common/state/OperatorDataState.ts';
import { LabeledText } from '../../ui/labeled-elements/LabeledText.tsx';
import { SingleSelection } from '../../ui/labeled-elements/SingleSelection.tsx';
import { TextField } from '../../ui/labeled-elements/TextField.tsx';
import { texts } from '../i18n/SharedTexts.ts';
import { useLanguage } from 'dg-web-shared/lib/Localization.tsx';

export interface Texts {
    validFrom: Translation;
    validTo: Translation;
    personalNumber: Translation;
    contractNumber: Translation;
    infos: Translation;
}

interface InfoFieldProps {
    value: string;
    onChange: (v: string) => void;
    headerPrefix?: string;
    disabled: boolean;
}

function useOperatorData() {
    const { storeState } = useStore(store => ({
        operator: OperatorDataState.get(store),
    }));
    return storeState.operator;
}

export function PersonalNumber({ value, onChange, disabled }: InfoFieldProps) {
    const operator = useOperatorData().data;
    const { language } = useLanguage();

    if (!operator) {
        return null;
    } else if (
        operator.licensePlatePermitSettings.whitelistPersonalNumber ===
        FieldSetting.hidden
    ) {
        return null;
    } else {
        return (
            <TextField
                value={value}
                label={texts[language].personalNumber()}
                onChange={onChange}
                inputType="text"
                disabled={disabled}
            />
        );
    }
}

export function ContractNumber({ value, onChange, disabled }: InfoFieldProps) {
    const operator = useOperatorData().data;
    const { language } = useLanguage();

    if (!operator) {
        return null;
    } else if (
        operator.licensePlatePermitSettings.whitelistContractNumber ===
        FieldSetting.hidden
    ) {
        return null;
    } else {
        return (
            <TextField
                value={value}
                label={texts[language].contractNumber()}
                onChange={onChange}
                inputType="text"
                disabled={disabled}
            />
        );
    }
}

export function AdditionalInfos({
    value,
    onChange,
    headerPrefix,
    disabled,
    mode,
}: InfoFieldProps & { mode: AdditionalInfoMode; disabled: boolean }) {
    const { language } = useLanguage();

    const label = (headerPrefix || '') + ' ' + texts[language].infos();
    if (mode === 'show') {
        return <LabeledText label={label}>{value}</LabeledText>;
    } else {
        return (
            <TextField
                value={value}
                multiline={true}
                label={label}
                onChange={onChange}
                inputType="text"
                disabled={disabled}
            />
        );
    }
}

interface FromDateSelectionProps {
    focused: boolean;
    onClick: () => void;
    validFrom: DateTime;
    language: string;
    disabled: boolean;
}

export function FromDateSelection({
    focused,
    onClick,
    validFrom,
    language,
    disabled,
}: FromDateSelectionProps) {
    const label = texts[language].validFrom();
    const content = Formatter.dayMonthYear(validFrom);
    if (validFrom < DateTime.now().startOf('day')) {
        return <LabeledText label={label}>{content}</LabeledText>;
    }
    return (
        <SingleSelection
            focused={focused}
            label={label}
            onClick={onClick}
            selection={content}
            disabled={disabled}
        />
    );
}

interface ToDateSelectionProps {
    focused: boolean;
    onClick: () => void;
    validTo: Maybe<DateTime>;
    onClear: Maybe<() => void>;
    language: string;
    disabled: boolean;
}

export function ToDateSelection({
    focused,
    onClick,
    validTo,
    onClear,
    language,
    disabled,
}: ToDateSelectionProps) {
    return (
        <SingleSelection
            focused={focused}
            label={texts[language].validTo()}
            onClick={onClick}
            onClear={onClear}
            selection={
                !validTo || isUnlimitedToDate(validTo)
                    ? '∞'
                    : Formatter.dayMonthYear(validTo)
            }
            disabled={disabled}
        />
    );
}

interface FromDatePickerSlideInProps {
    open: boolean;
    language: string;
    validFrom: DateTime | null;
    validTo: Maybe<DateTime>;
    onClose: () => void;
    onSelect: (d: DateTime) => void;
}

export const FromDatePickerSlideIn = (
    p: FromDatePickerSlideInProps,
): JSX.Element => {
    return (
        <DatePickerSlideIn
            open={p.open}
            caption={texts[p.language].validFrom()}
            onClose={p.onClose}
            selectedDate={p.validFrom}
            onSelect={p.onSelect}
            language={p.language}
            minDate={DateTime.now().startOf('day')}
            maxDate={p.validTo}
            outsideBody
        />
    );
};

interface ToDatePickerSlideInProps {
    open: boolean;
    language: string;
    validTo: Maybe<DateTime>;
    validFrom: DateTime;
    onClose: () => void;
    onSelect: (d: DateTime) => void;
}

export const ToDatePickerSlideIn = (
    p: ToDatePickerSlideInProps,
): JSX.Element => {
    return (
        <DatePickerSlideIn
            open={p.open}
            caption={texts[p.language].validTo()}
            onClose={p.onClose}
            selectedDate={p.validTo}
            onSelect={p.onSelect}
            language={p.language}
            minDate={
                p.validFrom > DateTime.now().startOf('day')
                    ? p.validFrom
                    : DateTime.now().startOf('day')
            }
            maxDate={DateTime.fromISO('2037-12-31')}
            outsideBody
        />
    );
};
